import type {BrandTokens, OlympusBorderWidths, OlympusRadii, OlympusShadows} from '@olympus-frontend/tenant-config';

export const DEFAULT_RADII: OlympusRadii = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 24,
  '4xl': 50,
  full: 9999,
  button: 9999,
  card: 24,
  images: 24,
};

export const DEFAULT_BORDER_WIDTHS: OlympusBorderWidths = {thin: 1, thick: 2};

export const DEFAULT_SHADOWS: OlympusShadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.06)',
  card: '0 0 30px rgba(0, 0, 0, 0.07)',
};

export const baseTokens: BrandTokens = {
  palette: {
    colors: {
      surface: {
        darkOverlay: '#18181B80',
        darkOverlayStrong: '#18181BCC',
        darkOverlayStrongHover: '#18181B99',
        lightOverlay: '#FFFFFF66',
        label: '#27272A',
        soldoutLabel: '#FFFFFF',
        brandOverlay: '#0D47A1CC',
        neutral: {
          page: '#FAFAFA',
          pageAlternative: '#F4F4F5',
          default: '#FFFFFF',
          subtle: '#F4F4F5',
          disabled: '#E4E4E7',
          inverse: '#18181B',
          inverseMuted: '#27272A',
          hover: '#F4F4F5',
          active: '#E4E4E7',
          inverseSubtle: '#FFFFFF1A',
          inverseSubtleHover: '#FFFFFF33',
        },
        primary: {
          default: '#1976D2',
          hover: '#0D47A1',
          subtle: '#E3F2FD',
          subtleHover: '#BBDEFB',
          active: '#0D47A1',
          disabled: '#E4E4E7',
        },
        secondary: {
          default: '#27272A',
          hover: '#18181B',
          subtle: '#F4F4F5',
          subtleHover: '#E4E4E7',
          active: '#18181B',
        },
        header: {
          default: '#18181B',
        },
        footer: {
          default: '#18181B',
        },
        section: {
          emphasis: '#27272A',
          emphasisDark: '#18181B',
          overlay: '#E3F2FDCC',
        },
        info: {
          default: '#3B82F6',
          subtle: '#DBEAFE',
        },
        success: {
          default: '#1EAC69',
          subtle: '#D2EEE1',
        },
        warning: {
          default: '#E87C17',
          subtle: '#FAE5D1',
        },
        error: {
          default: '#D43A3D',
          subtle: '#F6D8D8',
        },
      },
      text: {
        soldoutLabelText: '#18181B',
        neutral: {
          strong: '#18181B',
          default: '#27272A',
          disabled: '#A1A1AA',
          muted: '#52525B',
          subtle: '#A1A1AA',
          placeholder: '#71717A',
          inverse: '#FFFFFF',
          inverseMuted: '#D4D4D8',
        },
        primary: {
          default: '#0D47A1',
          hover: '#1565C0',
          onColor: '#FFFFFF',
          onSubtle: '#1565C0',
          inverse: '#64B5F6',
          active: '#1565C0',
        },
        secondary: {
          default: '#18181B',
          hover: '#27272A',
          onColor: '#FFFFFF',
          onSubtle: '#18181B',
        },
        info: {
          default: '#2563EB',
          onColor: '#FFFFFF',
          onSubtle: '#2563EB',
        },
        success: {
          default: '#188A54',
          onColor: '#FFFFFF',
          onSubtle: '#188A54',
        },
        warning: {
          default: '#BA6312',
          onColor: '#18181B',
          onSubtle: '#BA6312',
        },
        error: {
          default: '#D43A3D',
          onColor: '#FFFFFF',
          onSubtle: '#AA2E31',
        },
      },
      border: {
        neutral: {
          default: '#E4E4E7',
          disabled: '#A1A1AA',
          subtle: '#F4F4F5',
          strong: '#52525B',
          inverse: '#FFFFFF1A',
          hover: '#D4D4D8',
          inverseStrong: '#FFFFFF',
        },
        primary: {
          default: '#1976D2',
          hover: '#0D47A1',
          subtle: '#E3F2FD',
        },
        secondary: {
          default: '#27272A',
        },
        info: {
          default: '#3B82F6',
        },
        success: {
          default: '#1EAC69',
        },
        warning: {
          default: '#E87C17',
        },
        error: {
          default: '#D43A3D',
          hover: '#AA2E31',
        },
        focus: {
          default: '#90CAF9',
          onColor: '#FFFFFF',
        },
      },
      icon: {
        neutral: {
          strong: '#52525B',
          default: '#18181B',
          muted: '#52525B',
          disabled: '#A1A1AA',
          inverse: '#FFFFFF',
        },
        primary: {
          default: '#0D47A1',
          onColor: '#FFFFFF',
          hover: '#0D47A1',
          onSubtle: '#0D47A1',
          inverse: '#64B5F6',
        },
        secondary: {
          default: '#18181B',
          onColor: '#FFFFFF',
          hover: '#27272A',
          onSubtle: '#18181B',
        },
        info: {
          default: '#3B82F6',
          onColor: '#FFFFFF',
        },
        success: {
          default: '#1EAC69',
          onColor: '#FFFFFF',
        },
        warning: {
          default: '#E87C17',
          onColor: '#18181B',
        },
        error: {
          default: '#D43A3D',
          onColor: '#FFFFFF',
        },
      },
      base: {
        white: '#FFFFFF',
        black: '#000000',
      },
    },
  },
  font: {
    family: {
      primary: 'Figtree, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      secondary: 'Figtree, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    size: {
      'caption-2': 10,
      caption: 12,
      label: 13,
      'body-3': 14,
      'body-2': 16,
      'body-1': 18,
      'title-6': 20,
      'title-5': 22,
      'title-4': 24,
      'title-3': 26,
      'title-2': 28,
      'title-1': 36,
      'headline-2': 38,
      'headline-1': 40,
      display: 55,
    },
    weight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
    },
    lineHeight: {
      '20': '20px',
      '22': '22px',
      '24': '24px',
    },
    style: {
      'semibold-italic': 'SemiBold Italic',
    },
  },
  fontFamilyArabic: 'Tajawal, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  radii: DEFAULT_RADII,
  borderWidths: DEFAULT_BORDER_WIDTHS,
  shadows: DEFAULT_SHADOWS,
  shape: {borderRadius: 8},
  components: {
    // Container is a centered wrapper with responsive horizontal padding,
    // capped at the 1440 Figma desktop frame. maxWidth=false drops MUI's
    // breakpoint width caps and disableGutters drops its built-in padding so
    // paddingInline below is the only gutter (logical property keeps RTL
    // correct). At lg the 140px gutter + 1160px content fills exactly 1440, so
    // beyond that the container centres with auto margins.
    MuiContainer: {
      defaultProps: {maxWidth: false, disableGutters: true},
      styleOverrides: {
        root: ({theme}) => ({
          marginInline: 'auto',
          width: '100%',
          paddingInline: theme.spacing(2.25),
          [theme.breakpoints.up('md')]: {
            paddingInline: '32px',
          },
          [theme.breakpoints.up('lg')]: {
            maxWidth: 1160 + 32 * 2,
          },
        }),
      },
    },
  },
};
